import type { Profile, Snapshot } from './models';

// Fictional beta profiles. Stable IDs make this an additive, repeatable seed upgrade.
const people = [
  [
    'Nina Patel',
    'Bristol, UK',
    'Brand designer',
    'Making refill shops easier to find and easier to love.',
  ],
  [
    'Oscar Bennett',
    'Leeds, UK',
    'Sales lead',
    'Helping repair businesses reach their next hundred customers.',
  ],
  [
    'Sofia Costa',
    'Porto, PT',
    'Frontend engineer',
    'Building calmer booking tools for small community studios.',
  ],
  [
    'Idris Rahman',
    'London, UK',
    'Operations specialist',
    'Turning spare commercial kitchen time into local opportunity.',
  ],
  [
    'Freya Olsen',
    'Copenhagen, DK',
    'Service designer',
    'Exploring neighbourhood libraries for things we rarely use.',
  ],
  [
    'Daniel Brooks',
    'Bath, UK',
    'Product manager',
    'Testing simpler ways for volunteers to organise their week.',
  ],
  [
    'Zara Ahmed',
    'Manchester, UK',
    'Community builder',
    'Connecting independent teachers with curious local learners.',
  ],
  [
    'Luca Romano',
    'Milan, IT',
    'Software engineer',
    'Making the everyday admin of a small workshop disappear.',
  ],
  [
    'Grace Kim',
    'Edinburgh, UK',
    'Research lead',
    'Learning how families choose affordable low-waste routines.',
  ],
  [
    'Theo Martin',
    'Brighton, UK',
    'Content strategist',
    'Helping local makers tell the story behind what they build.',
  ],
  [
    'Amara Nwosu',
    'Birmingham, UK',
    'Customer success lead',
    'Turning first customers into useful product conversations.',
  ],
  [
    'Hugo Silva',
    'Lisbon, PT',
    'Industrial designer',
    'Prototyping repairable everyday products with small teams.',
  ],
  [
    'Mila Novak',
    'Prague, CZ',
    'Data engineer',
    'Making independent shops understand their busiest hours.',
  ],
  [
    'Ethan Clarke',
    'Sheffield, UK',
    'Workshop owner',
    'Testing practical tools with people who work with their hands.',
  ],
  [
    'Ines Laurent',
    'Lyon, FR',
    'Growth marketer',
    'Helping neighbourhood food businesses find repeat customers.',
  ],
  [
    'Ravi Menon',
    'Cambridge, UK',
    'Product designer',
    'Designing approachable tools for people learning something new.',
  ],
  [
    'Chloe Evans',
    'Cardiff, UK',
    'Partnerships lead',
    'Bringing small organisations together around shared space.',
  ],
  [
    'Mateo Garcia',
    'Valencia, ES',
    'Mobile engineer',
    'Building offline tools for teams working out in the world.',
  ],
  [
    'Aisha Khan',
    'Nottingham, UK',
    'Service designer',
    'Making community wellbeing services easier to navigate.',
  ],
  [
    'Felix Weber',
    'Hamburg, DE',
    'Operations manager',
    'Finding a useful second life for surplus building materials.',
  ],
  [
    'Elise Dubois',
    'Paris, FR',
    'Brand strategist',
    'Helping early teams explain one useful idea clearly.',
  ],
  [
    'Samira Ali',
    'Liverpool, UK',
    'Programme manager',
    'Building stronger links between schools and local makers.',
  ],
  [
    'Ben Carter',
    'Oxford, UK',
    'Backend engineer',
    'Small reliable systems for businesses doing important work.',
  ],
  [
    'Yuki Tanaka',
    'London, UK',
    'Interaction designer',
    'Creative tools that invite experimentation without a manual.',
  ],
  [
    'Olivia Scott',
    'York, UK',
    'Customer researcher',
    'Starting with the people who will actually use what we make.',
  ],
  [
    'Gabriel Santos',
    'Madrid, ES',
    'Sales specialist',
    'Finding the first customers for thoughtful climate products.',
  ],
  [
    'Hannah Reid',
    'Glasgow, UK',
    'Community organiser',
    'Making shared neighbourhood gardens easier to coordinate.',
  ],
  [
    'Arjun Mehta',
    'Bristol, UK',
    'Automation engineer',
    'Removing the repetitive work from independent garages.',
  ],
  [
    'Eva Kowalska',
    'Warsaw, PL',
    'Product lead',
    'Testing affordable ways for small teams to run experiments.',
  ],
  [
    'Louis Bernard',
    'Lille, FR',
    'Hospitality operator',
    'Helping independent cafes share equipment and experience.',
  ],
  [
    'Nadia Hassan',
    'Coventry, UK',
    'UX designer',
    'Better everyday digital tools for carers and families.',
  ],
  [
    'Finn Murphy',
    'Dublin, IE',
    'Growth lead',
    'Turning weekend prototypes into conversations with customers.',
  ],
  [
    'Leila Haddad',
    'Reading, UK',
    'Frontend developer',
    'Building accessible tools for local learning communities.',
  ],
  [
    'Max Turner',
    'Newcastle, UK',
    'Manufacturing specialist',
    'Making it practical to repair products close to home.',
  ],
  [
    'Tessa Visser',
    'Utrecht, NL',
    'Service designer',
    'Helping people share skills across their neighbourhood.',
  ],
  [
    'Kwame Mensah',
    'London, UK',
    'Partnerships manager',
    'Connecting food founders with the spaces they need to grow.',
  ],
  [
    'Isabel Ruiz',
    'Seville, ES',
    'Product researcher',
    'Exploring healthier routines that fit real working lives.',
  ],
  [
    'Jonas Berg',
    'Stockholm, SE',
    'Software developer',
    'Simple planning tools for creative teams with limited time.',
  ],
  [
    'Ruby Walker',
    'Exeter, UK',
    'Marketing strategist',
    'Helping small sustainable brands earn their first loyal fans.',
  ],
  [
    'Omar Farouk',
    'Leicester, UK',
    'Business operator',
    'Bringing the right people together to test a useful local idea.',
  ],
] as const;
const strengths = [
  ['Design', 'Product'],
  ['Sales', 'Networking'],
  ['Engineering', 'Product'],
  ['Operations', 'Execution'],
  ['Design', 'Research'],
  ['Marketing', 'Strategy'],
  ['Content', 'Networking'],
  ['Engineering', 'Automotive'],
];
const interests = [
  'Climate',
  'Automotive',
  'Education',
  'Food & hospitality',
  'Local services',
  'Creative tools',
  'Health & wellbeing',
];
const avatars: Profile['avatar'][] = ['sage', 'coral', 'sky', 'sand', 'lavender'];
export const EXTRA_DEMO_CONTACTS = people.length;
export function withDemoContacts(s: Snapshot, now = new Date()): Snapshot {
  if (!s.profiles.some((p) => p.id === 'jamie')) return s;
  const existing = new Set(s.profiles.map((p) => p.id));
  const added: Profile[] = people.flatMap(([name, location, background, bio], i) => {
    const id = `demo-founder-${i + 1}`;
    if (existing.has(id)) return [];
    const skills = strengths[i % strengths.length]!;
    const seeking = skills.includes('Engineering')
      ? ['Sales', 'Design']
      : ['Engineering', 'Product'];
    return [
      {
        id,
        name,
        location,
        background,
        bio,
        skills,
        seeking,
        avatar: avatars[i % avatars.length]!,
        industries: [interests[i % interests.length]!, 'Climate'],
        brings: skills,
        wants: seeking,
        hours: 6 + (i % 15),
        preference: i % 5 === 0 ? 'Hybrid' : 'Remote',
        stage: 'Building MVP',
        style: i % 3 === 0 ? 'Regular check-ins' : 'Async first',
        commitment: 'Side project',
        idea: i % 2 ? 'Have an idea' : 'Either',
        teamSize: 3,
        looking: true,
        is_ai: false,
      },
    ];
  });
  if (!added.length) return s;
  return {
    ...s,
    profiles: [...s.profiles, ...added],
    connections: [
      ...s.connections,
      ...added
        .filter((p) => Number(p.id.split('-').at(-1)) % 5 === 1)
        .map((p) => ({
          id: `demo-like-${p.id}`,
          from: p.id,
          to: 'jamie',
          status: 'pending' as const,
          intro: '',
          createdAt: now.toISOString(),
        })),
    ],
  };
}
